import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Separator } from '@parrot-software/design-system';
import { Lock } from '@parrot-software/design-system/icons';
import { Controller, useWatch } from 'react-hook-form-v7';
import type { UseFormReturn } from 'react-hook-form-v7';
import { cn } from '@parrot-software/utils';

import type { NotificationPreferenceConfigDto, NotificationPreferencesConfigDto } from '@personnel/application/dtos';
import { NOTIFICATION_CHANNEL_ORDER } from '@personnel/domain/types';
import type { NotificationChannel } from '@personnel/domain/types';
import { useEmployeeFormContext } from '@personnel/presentation/components/form/EmployeeForm/hooks';
import { styles, DisabledSectionHelp } from '@personnel/presentation/components/form/EmployeeForm/shared';
import {
  PERMISSIONS_THAT_REQUIRE_CREDENTIALS,
  getFrontendIdsForBackendIds,
} from '@personnel/presentation/components/form/EmployeeForm/utils';
import {
  resolveChannelState,
  splitNotificationGroups,
} from '@personnel/presentation/mappers/notificationPreferencesMapper';
import type { EmployeeFormData } from '@personnel/presentation/types';

interface NotificationsSectionProps {
  form: UseFormReturn<EmployeeFormData>;
  /** What the organization offers per group; it decides which rows exist and which are locked. */
  notificationConfig: NotificationPreferencesConfigDto;
}

const ROW_CLASSES = 'grid grid-cols-[1fr_5rem_5rem] items-center gap-4 py-2';

/**
 * The channel headers both blocks repeat.
 *
 * @param {object} props - Component properties
 * @param {boolean} [props.muted] - Whether the block below is disabled
 */
const ChannelHeaders = ({ muted }: { muted?: boolean }): React.ReactElement => {
  const { t } = useTranslation('personnel-employees');

  return (
    <div className={ROW_CLASSES}>
      <span />
      {NOTIFICATION_CHANNEL_ORDER.map((channel) => (
        <span
          className={cn('text-center text-xs font-semibold tracking-wide text-muted-foreground uppercase', {
            'opacity-60': muted,
          })}
          key={channel}
        >
          {t(`form.notifications.channels.${channel}`)}
        </span>
      ))}
    </div>
  );
};

/**
 * One group of the matrix: its wording and one checkbox per channel.
 *
 * Every checkbox is a form field, the locked ones included, because the write sends the whole
 * matrix. A locked field never becomes a change: it loads with the value the configuration
 * dictates and the screen offers no way to move it.
 *
 * @param {object} props - Component properties
 * @param {NotificationPreferenceConfigDto} props.entry - What this group offers
 * @param {UseFormReturn<EmployeeFormData>} props.form - The employee form
 * @param {boolean} [props.isSectionDisabled] - Whether the whole block is switched off
 */
const NotificationGroupRow = ({
  entry,
  form,
  isSectionDisabled,
}: {
  entry: NotificationPreferenceConfigDto;
  form: UseFormReturn<EmployeeFormData>;
  isSectionDisabled?: boolean;
}): React.ReactElement => {
  const { t } = useTranslation('personnel-employees');
  const { control } = useEmployeeFormContext(form);
  const label = t(`form.notifications.groups.${entry.notificationGroup}.label`);

  return (
    <div className={ROW_CLASSES}>
      <div className="flex flex-col gap-1">
        <span className={cn('text-sm font-medium text-foreground', { 'text-muted-foreground': isSectionDisabled })}>
          {label}
        </span>
        <span className="text-xs text-muted-foreground">
          {t(`form.notifications.groups.${entry.notificationGroup}.description`)}
        </span>
      </div>
      {NOTIFICATION_CHANNEL_ORDER.map((channel: NotificationChannel) => (
        <Controller
          control={control}
          key={channel}
          name={`notificationPreferences.${entry.notificationGroup}.${channel}`}
          render={({ field }) => {
            const state = resolveChannelState(entry, channel, !!field.value);

            return (
              <div className="flex justify-center">
                <Checkbox
                  aria-label={`${label} ${t(`form.notifications.channels.${channel}`)}`}
                  checked={state.isChecked}
                  data-testid={`notification-${entry.notificationGroup}-${channel}`}
                  disabled={state.isLocked || isSectionDisabled}
                  id={`${entry.notificationGroup}-${channel}`}
                  onCheckedChange={(checked) => field.onChange(!!checked)}
                />
              </div>
            );
          }}
        />
      ))}
    </div>
  );
};

/**
 * The rows of one block, with a line between each pair. There is no line after the last one: it
 * would read as the start of a row that never comes.
 *
 * @param {object} props - Component properties
 * @param {NotificationPreferencesConfigDto} props.entries - The groups this block paints
 * @param {UseFormReturn<EmployeeFormData>} props.form - The employee form
 * @param {boolean} [props.isSectionDisabled] - Whether the whole block is switched off
 */
const NotificationGroupRows = ({
  entries,
  form,
  isSectionDisabled,
}: {
  entries: NotificationPreferencesConfigDto;
  form: UseFormReturn<EmployeeFormData>;
  isSectionDisabled?: boolean;
}): React.ReactElement => (
  <>
    {entries.map((entry, index) => (
      <Fragment key={entry.notificationGroup}>
        {index > 0 && <Separator />}
        <NotificationGroupRow entry={entry} form={form} isSectionDisabled={isSectionDisabled} />
      </Fragment>
    ))}
  </>
);

/**
 * Which notifications this user receives, and by which channel.
 *
 * Neither the rows nor the columns are decided here: the configuration says which groups exist,
 * which channels each one offers and which of them cannot be switched off. A group with a
 * mandatory channel is an always-on group, and that is the only thing that tells the two blocks
 * apart.
 *
 * @param {NotificationsSectionProps} props - Component properties
 */
export const NotificationsSection = React.memo(function NotificationsSection({
  form,
  notificationConfig,
}: NotificationsSectionProps): React.ReactElement {
  const { t } = useTranslation('personnel-employees');
  const { t: tRoles } = useTranslation('roles');
  const { control } = useEmployeeFormContext(form);

  const credentialsEnabled = useWatch({ control, name: '_credentialsEnabled' });
  const isDisabled = !credentialsEnabled;

  const { editable, alwaysOn } = useMemo(() => splitNotificationGroups(notificationConfig), [notificationConfig]);

  const requiredPermissionNames = useMemo(() => {
    const frontendIds = getFrontendIdsForBackendIds(PERMISSIONS_THAT_REQUIRE_CREDENTIALS);
    return frontendIds.map((frontendId) => tRoles(`permissions.${frontendId}.name`)).join(', ');
  }, [tRoles]);

  return (
    <>
      {editable.length > 0 && (
        <div className={cn(styles.container, 'gap-3')}>
          <h2 className={cn(styles.header.cardTitle, 'mb-0', { 'text-muted-foreground': isDisabled })}>
            {t('form.notifications.title')}
          </h2>

          <div>
            <ChannelHeaders muted={isDisabled} />
            <NotificationGroupRows entries={editable} form={form} isSectionDisabled={isDisabled} />
            {isDisabled && requiredPermissionNames && (
              <DisabledSectionHelp
                i18nKey="form.contactSection.credentialsDisabledHelp"
                values={{ permissions: requiredPermissionNames }}
              />
            )}
          </div>
        </div>
      )}

      {alwaysOn.length > 0 && (
        <div className={cn(styles.container, 'gap-3')}>
          <div>
            <div className="flex items-center gap-2">
              <h2 className={cn(styles.header.cardTitle, 'mb-0')}>{t('form.notifications.alwaysOn.title')}</h2>
              <Lock aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            </div>
            <p className={styles.header.description}>{t('form.notifications.alwaysOn.description')}</p>
          </div>

          <div>
            <ChannelHeaders />
            <NotificationGroupRows entries={alwaysOn} form={form} />
          </div>
        </div>
      )}
    </>
  );
});
